import type { ProfileRow } from './profile'
import { supabase } from './supabaseClient'
import i18n from '../i18n/index'
import { protocolWasPersisted } from './protocolIntegrity'

export type ProtocolGenerationPhase =
  | 'metabolics_calculated'
  | 'training_sequenced'
  | 'macros_tiered'
  | 'meals_sequenced'
  | 'meal_images_ready'
  | 'budget_matched'

export type GenerateResult = {
  error: string | null
  connectionError?: boolean
  source?: 'ai' | 'fallback' | 'edge'
}

export function connectionErrorMessage(): string {
  return i18n.t('app.errors.connectionIssue')
}

/** @deprecated use connectionErrorMessage() for localized copy */
export const CONNECTION_ERROR_MESSAGE = connectionErrorMessage()

const STREAM_PHASES = new Set<ProtocolGenerationPhase>([
  'metabolics_calculated',
  'training_sequenced',
  'macros_tiered',
  'meals_sequenced',
  'meal_images_ready',
  'budget_matched',
])

function parseSseChunk(buffer: string): { events: Record<string, unknown>[]; remainder: string } {
  const events: Record<string, unknown>[] = []
  const parts = buffer.split('\n\n')
  const remainder = parts.pop() ?? ''

  for (const part of parts) {
    for (const line of part.split('\n')) {
      if (!line.startsWith('data: ')) continue
      try {
        events.push(JSON.parse(line.slice(6)) as Record<string, unknown>)
      } catch {
        // ignore malformed chunks
      }
    }
  }

  return { events, remainder }
}

async function readHttpError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string; message?: string }
    const message = body.error ?? body.message
    if (typeof message === 'string' && message.trim()) return message
  } catch {
    try {
      const text = await response.text()
      if (text.trim()) return text.slice(0, 300)
    } catch {
      // ignore
    }
  }
  return `Protocol generation failed (${response.status})`
}

async function protocolWasPersistedForProfile(profile: ProfileRow): Promise<boolean> {
  return protocolWasPersisted(profile.weekly_commitment)
}

async function recoverAfterStreamDrop(
  accessToken: string,
  profile: ProfileRow,
): Promise<GenerateResult | null> {
  if (await protocolWasPersistedForProfile(profile)) {
    console.warn('generateInitialProtocol: stream dropped but protocol exists — treating as success')
    return { error: null, source: 'edge' }
  }

  console.warn('generateInitialProtocol: stream dropped — retrying non-streaming')
  const retry = await invokeNonStreamingProtocol(accessToken)
  if (!retry.error) {
    if (await protocolWasPersistedForProfile(profile)) return retry
    return { error: connectionErrorMessage(), connectionError: true }
  }

  if (await protocolWasPersistedForProfile(profile)) {
    console.warn('generateInitialProtocol: non-stream retry failed but protocol exists — treating as success')
    return { error: null, source: 'edge' }
  }

  return null
}

async function invokeNonStreamingProtocol(accessToken: string): Promise<GenerateResult> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

  let response: Response
  try {
    response = await fetch(`${supabaseUrl}/functions/v1/generate-initial-protocol`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: supabaseAnonKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ stream: false }),
    })
  } catch (err) {
    console.error('generateInitialProtocol non-stream fetch failed:', err)
    return { error: connectionErrorMessage(), connectionError: true }
  }

  if (!response.ok) {
    const message = await readHttpError(response)
    return {
      error: message,
      connectionError: response.status >= 500 || response.status === 408 || response.status === 429,
    }
  }

  try {
    const body = (await response.json()) as { ok?: boolean; source?: GenerateResult['source']; error?: string }
    if (body.ok) {
      return { error: null, source: body.source ?? 'edge' }
    }
    return { error: body.error ?? 'Generation failed' }
  } catch {
    return { error: connectionErrorMessage(), connectionError: true }
  }
}

/** Streams Supabase Edge Function phases while generating the initial protocol. */
export async function generateInitialProtocol(
  userId: string,
  profile: ProfileRow,
  onPhase?: (phase: ProtocolGenerationPhase) => void,
): Promise<GenerateResult> {
  void userId

  const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
  if (sessionError || !sessionData.session) {
    return { error: 'Not authenticated', connectionError: true }
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

  let response: Response
  try {
    response = await fetch(`${supabaseUrl}/functions/v1/generate-initial-protocol`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${sessionData.session.access_token}`,
        apikey: supabaseAnonKey,
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ stream: true }),
    })
  } catch (err) {
    console.error('generateInitialProtocol fetch failed:', err)
    return { error: connectionErrorMessage(), connectionError: true }
  }

  if (!response.ok || !response.body) {
    const message = await readHttpError(response)
    console.error('generateInitialProtocol HTTP error:', response.status, message)
    return {
      error: message,
      connectionError: response.status >= 500 || response.status === 408 || response.status === 429,
    }
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let sawComplete = false
  let source: GenerateResult['source'] = 'edge'

  const handleEvent = (event: Record<string, unknown>) => {
    const status = event.status
    if (typeof status !== 'string') return

    if (status === 'error') {
      const serverError = typeof event.error === 'string' ? event.error : 'Generation failed'
      throw new Error(serverError)
    }

    if (status === 'complete') {
      sawComplete = true
      const data = event.data as Record<string, unknown> | undefined
      source = (data?.source as GenerateResult['source']) ?? 'edge'
      return
    }

    if (STREAM_PHASES.has(status as ProtocolGenerationPhase)) {
      onPhase?.(status as ProtocolGenerationPhase)
    }
  }

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const { events, remainder } = parseSseChunk(buffer)
      buffer = remainder

      for (const event of events) {
        handleEvent(event)
      }
    }

    if (buffer.trim()) {
      const { events } = parseSseChunk(`${buffer}\n\n`)
      for (const event of events) {
        handleEvent(event)
      }
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : connectionErrorMessage()
    console.error('generateInitialProtocol stream error:', err)
    return {
      error: message,
      connectionError: message === connectionErrorMessage(),
    }
  }

  if (!sawComplete) {
    console.error('generateInitialProtocol stream ended without complete event')
    const recovered = await recoverAfterStreamDrop(sessionData.session.access_token, profile)
    if (recovered) return recovered
    return { error: connectionErrorMessage(), connectionError: true }
  }

  if (!(await protocolWasPersistedForProfile(profile))) {
    console.error('generateInitialProtocol complete event received but protocol not persisted')
    return { error: connectionErrorMessage(), connectionError: true }
  }

  return { error: null, source }
}
